// Shared per-week defaults for the TracPatch REP MILESTONE table.
// Used by both tracpatch.override.form.js (the editable table cells) and
// tracpatch.config.modal.js (the patient preview's band derivation) so the
// values shown in the table and the bands drawn in the preview stay in sync
// when no per-week override row is stored for the selected week.

// Canonical chain: ext_limit ≤ ext_high ≤ ext_low ≤ flex_low ≤ flex_high ≤ flex_limit.
// Naming on the table:
//   ext_high = smaller-numerical / more extended gate ("Higher Ext")
//   ext_low  = larger-numerical  / less  extended gate ("Lower Ext")
export const WEEK_ANGLE_FIELDS = ['flexion_low', 'flexion_high', 'extension_low', 'extension_high']
export const WEEK_ROW_FIELDS = [...WEEK_ANGLE_FIELDS, 'reps']

// Canonical post-op-week → tier boundaries. tierDefaults rows may step the 4
// inner angle gates (the override-form angle cells), reps, and rep_milestone
// per tier; for any field a row omits, the cell resolves to master. The hard
// limits (extension_limit, flexion_limit) are never in tierDefaults. Tier 2
// spans weeks 5-9 (matches the seed's tierDefaults rows).
export const TIER_WEEK_RANGES = [
  { tier: 1, from: 0, to: 4 },
  { tier: 2, from: 5, to: 9 },
  { tier: 3, from: 10, to: null },
]

export const tierForWeek = (week) => {
  for (const r of TIER_WEEK_RANGES) {
    if (week >= r.from && (r.to == null || week <= r.to)) return r.tier
  }
  return TIER_WEEK_RANGES[TIER_WEEK_RANGES.length - 1].tier
}

export const cellFallbackField = (field) =>
  field === 'reps' ? 'requiredReps' : field

// Find the tierDefaults row whose inclusive [weekFrom, weekTo] range contains
// `week` (weekTo null = open-ended). Returns null when there is no table or no
// matching row (→ caller falls back to master).
function findTierRow(tierDefaults, week) {
  if (!Array.isArray(tierDefaults)) return null
  for (const row of tierDefaults) {
    if (!row) continue
    const from = row.weekFrom
    const to = row.weekTo
    if (week >= from && (to == null || week <= to)) return row
  }
  return null
}

// Tiered per-week default for a (week, field) cell when no per-week override
// row is stored. Pure function — caller passes a `resolved(field)` resolver
// that returns the override-or-master value for any field, plus the exercise's
// global-baseline `tierDefaults` table (from Exercise.tracpatchConfig). When
// the table has a row for this week that sets `field`, that wins; otherwise the
// master value is used. An exercise with no tierDefaults resolves to master for
// every week (the prior behavior of every exercise except Heel Slide). Both
// apps read the SAME tierDefaults structure, so the admin table and the patient
// session can no longer drift — see tieredWeekDefault() in
// physio_app_web/components/tracpatch_exercise/TracPatchSessionTemplate.js.
export function tieredDefault(week, field, ctx) {
  const resolved = ctx.resolved
  const master = resolved(cellFallbackField(field))
  if (master == null) return master

  // An explicit per-patient reps override beats the master tierDefaults —
  // mirrors overlayWeekEntry() in physio_app_web TracPatchSessionTemplate.js so
  // the admin REP MILESTONE table / preview show the same rep target the
  // patient session will actually use. Without this, a top-level requiredReps
  // override (e.g. 2) was shadowed by the tier row's reps (e.g. 10).
  if (
    cellFallbackField(field) === 'requiredReps' &&
    ctx.overrides &&
    ctx.overrides.requiredReps != null
  ) {
    return ctx.overrides.requiredReps
  }

  const row = findTierRow(ctx.tierDefaults, week)
  if (row && row[field] != null) return row[field]
  return master
}

// Clinic per-week layer, then tiered default. The clinic's per-week REP
// MILESTONE rows (ctx.clinicRepMilestoneByWeek, resolved server-side onto the
// master config as rep_milestone_by_week) sit BELOW any per-patient value but
// ABOVE the tiered/master default — so a clinic that sets a week's reps/angles
// is the baseline a patient inherits when they have no own override. Mirrors
// the clinic layer in physio_app_web TracPatchSessionTemplate.js overlayWeekEntry().
export function clinicOrTieredDefault(week, field, ctx) {
  const clinicList = ctx.clinicRepMilestoneByWeek || []
  const clinicEntry = clinicList.find((r) => r && r.week === week)
  if (clinicEntry && clinicEntry[field] !== undefined && clinicEntry[field] !== null) {
    return clinicEntry[field]
  }
  return tieredDefault(week, field, ctx)
}

// Resolve a (week, field) cell. Precedence:
//   per-patient per-week row → per-patient flat requiredReps (reps only) →
//   clinic per-week row → tiered default → master.
// The per-patient flat requiredReps beats the clinic per-week value (it's the
// most specific per-patient reps signal — mirrors overlayWeekEntry() in the
// patient app), so clinic per-week reps surface only when the patient has no
// own reps override for that week.
export function resolveWeekCell(week, field, ctx) {
  const list = ctx.repMilestoneByWeek || []
  const entry = list.find((r) => r && r.week === week)
  if (entry && entry[field] !== undefined && entry[field] !== null) {
    return entry[field]
  }
  if (
    field === 'reps' &&
    ctx.overrides &&
    ctx.overrides.requiredReps != null
  ) {
    return ctx.overrides.requiredReps
  }
  return clinicOrTieredDefault(week, field, ctx)
}

// Patient's current post-op week from surgery date.
//   surgeryDate in the future → -1 (Pre-Op)
//   surgeryDate today or past → floor(daysSince / 7), clamped to maxWeek
// No surgery date → 0 (so the modal still opens on something sensible).
export function currentWeekFromSurgery(surgeryDate, maxWeek) {
  const cap = maxWeek != null ? maxWeek : 13
  if (!surgeryDate) return 0
  const surgery = new Date(surgeryDate)
  if (Number.isNaN(surgery.getTime())) return 0
  const today = new Date()
  // Local-midnight normalization so the comparison sits on day boundaries
  // and a same-day surgery counts as day 0 regardless of clock time.
  const surgeryMid = new Date(surgery.getFullYear(), surgery.getMonth(), surgery.getDate()).getTime()
  const todayMid   = new Date(today.getFullYear(),   today.getMonth(),   today.getDate()).getTime()
  const daysSince = Math.round((todayMid - surgeryMid) / 86400000)
  if (daysSince < 0) return -1
  return Math.min(cap, Math.floor(daysSince / 7))
}
